/**
 * Author: Fleming Shi
 * Project: DAL (Digital Abstraction Layer)
 * Date: 2026-04-30
 * Description: Authenticator-app enrollment for verified users (optional).
 *              If the user already has an authenticator enrolled, we DON'T
 *              auto-overwrite their secret — we show "Active on device" and
 *              require an explicit Reset click to start over. Only one
 *              device is supported; resetting invalidates the previous
 *              enrollment.
 */

import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '@services/api'

const ApiAccessAuthSetup: React.FC = () => {
  const navigate = useNavigate()
  const [loadingMe, setLoadingMe] = useState(true)
  const [alreadyEnrolled, setAlreadyEnrolled] = useState(false)

  const [enrolling, setEnrolling] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [qr, setQr] = useState<string | null>(null)
  const [otpauth, setOtpauth] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  // First: figure out whether the user already has an authenticator enrolled.
  // If they do, we DO NOT call /totp/enroll on mount — that would overwrite
  // their working secret. They have to click Reset explicitly.
  useEffect(() => {
    api
      .accessRequestMe()
      .then((me) => {
        if (me.totp_enrolled) {
          setAlreadyEnrolled(true)
        } else {
          startEnrollment()
        }
      })
      .catch(() => setError('Sign-in cookie missing or expired. Sign in again, then come back.'))
      .finally(() => setLoadingMe(false))
  }, [])

  const startEnrollment = () => {
    setEnrolling(true)
    setError(null)
    setQr(null)
    setOtpauth(null)
    setCode('')
    api
      .accessRequestTotpEnroll()
      .then((data) => {
        setQr(data.qr_data_url)
        setOtpauth(data.otpauth_url)
        setAlreadyEnrolled(false)
      })
      .catch(() => setError('Sign-in cookie missing or expired. Sign in again, then come back.'))
      .finally(() => setEnrolling(false))
  }

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!/^\d{6}$/.test(code)) {
      setError('Enter the six-digit code from your authenticator app')
      return
    }
    setError(null)
    setConfirming(true)
    try {
      const result = await api.accessRequestTotpConfirm(code)
      setDone(true)
      const target = result.redirect ?? '/api/docs/external/'
      setTimeout(() => {
        window.location.href = target
      }, 1200)
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Invalid code, try again'
      setError(msg)
    } finally {
      setConfirming(false)
    }
  }

  if (loadingMe) {
    return <div className="mx-auto max-w-xl py-16 text-center text-sm text-gray-500 dark:text-gray-400">Loading…</div>
  }

  if (error && !qr && !alreadyEnrolled) {
    return (
      <div className="mx-auto max-w-xl py-16">
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-6 dark:border-yellow-800/50 dark:bg-yellow-900/30">
          <h1 className="text-xl font-semibold text-yellow-900 dark:text-yellow-300">Can&apos;t enroll right now</h1>
          <p className="mt-2 text-sm text-yellow-800 dark:text-yellow-300">{error}</p>
        </div>
      </div>
    )
  }

  if (done) {
    return (
      <div className="mx-auto max-w-xl py-16">
        <div className="rounded-lg border border-green-200 bg-green-50 p-6 dark:border-green-800/50 dark:bg-green-900/30">
          <h1 className="text-xl font-semibold text-green-900 dark:text-green-300">Authenticator enrolled</h1>
          <p className="mt-2 text-sm text-green-800 dark:text-green-300">
            Future sign-ins can use your authenticator app&apos;s 6-digit code instead of waiting for an email. Email
            codes still work too — your choice.
          </p>
        </div>
      </div>
    )
  }

  // ── Already-enrolled state ──────────────────────────────────────────────────
  if (alreadyEnrolled) {
    return (
      <div className="mx-auto max-w-xl py-12">
        <div className="rounded-lg border border-green-200 bg-green-50 p-5 dark:border-green-800/50 dark:bg-green-900/30">
          <h1 className="text-xl font-semibold text-green-900 dark:text-green-300">
            Authenticator app is active on this device
          </h1>
          <p className="mt-2 text-sm text-green-800 dark:text-green-300">
            You&apos;re all set — sign in any time with the 6-digit code from your authenticator. Email codes still work
            as a fallback.
          </p>
        </div>

        <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 p-5 dark:border-amber-800/50 dark:bg-amber-900/30">
          <h2 className="text-base font-semibold text-amber-900 dark:text-amber-300">Need to set up a new device?</h2>
          <p className="mt-1 text-sm text-amber-800 dark:text-amber-300">
            Only one device is supported. Resetting will <strong>invalidate</strong> your existing authenticator entry,
            and you&apos;ll need to scan a new QR code with your new device. Your existing entry stops working
            immediately.
          </p>
          <button
            onClick={startEnrollment}
            className="mt-4 inline-flex items-center rounded-md border border-amber-300 bg-white px-3 py-1.5 text-sm font-medium text-amber-900 shadow-sm hover:bg-amber-100 dark:bg-gray-800 dark:text-amber-300 dark:shadow-none dark:hover:bg-amber-900/40"
          >
            Reset and set up a new device
          </button>
        </div>

        <p className="mt-6 text-xs text-gray-500 dark:text-gray-400">
          Lost your phone?{' '}
          <button onClick={startEnrollment} className="text-indigo-600 underline">
            Reset and re-enroll
          </button>{' '}
          — or just continue using email codes.
        </p>
        <button
          onClick={() => navigate('/api-access/welcome')}
          className="mt-4 text-sm text-gray-600 underline dark:text-gray-400"
        >
          ← Back to welcome
        </button>
      </div>
    )
  }

  // ── Enrollment-in-progress state ───────────────────────────────────────────
  if (enrolling || (!qr && !error)) {
    return (
      <div className="mx-auto max-w-xl py-16 text-center text-sm text-gray-500 dark:text-gray-400">
        Generating your QR…
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-xl py-12">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
        Set up authenticator app{' '}
        <span className="text-base font-normal text-gray-500 dark:text-gray-400">(optional)</span>
      </h1>
      <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
        Skip email codes on future logins. You already have access — this is just a convenience.
      </p>
      <ol className="mt-4 list-decimal space-y-2 pl-5 text-sm text-gray-700 dark:text-gray-300">
        <li>Open Google Authenticator, Microsoft Authenticator, 1Password, or any TOTP app.</li>
        <li>Scan the QR code below (or paste the setup URI as a manual entry).</li>
        <li>Enter the 6-digit code your app generates to confirm enrollment.</li>
      </ol>

      <div className="mt-6 flex flex-col items-center rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
        {qr && <img src={qr} alt="Authenticator QR code" className="h-56 w-56" />}
        {otpauth && (
          <details className="mt-3 w-full text-xs text-gray-500 dark:text-gray-400">
            <summary className="cursor-pointer">Manual setup URI</summary>
            <code className="mt-1 block rounded bg-gray-50 p-2 font-mono break-all dark:bg-white/5">{otpauth}</code>
          </details>
        )}
      </div>

      <form onSubmit={handleConfirm} className="mt-6 space-y-3">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Enter 6-digit code from your app
        </label>
        <input
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          className="block w-40 rounded-md border-gray-300 text-center font-mono text-lg tracking-widest shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-600 dark:shadow-none"
          placeholder="000000"
        />
        {error && <p className="text-sm text-red-600">{error}</p>}
        <button
          type="submit"
          disabled={confirming}
          className="inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 disabled:opacity-50 dark:shadow-none"
        >
          {confirming ? 'Confirming…' : 'Confirm enrollment'}
        </button>
      </form>

      <p className="mt-6 text-xs text-gray-500 dark:text-gray-400">
        Not now? Just continue using email codes — they keep working. You can come back here any time.
      </p>
    </div>
  )
}

export default ApiAccessAuthSetup
